import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { filter, Subscription, forkJoin } from 'rxjs';

import { CompanySubscription, CompanySubscriptionStatus } from '@shared/models';
import { CompanySubscriptionService, CompanyLicenceService, ToastsService } from '@shared/services';

@Component({
  selector: 'app-no-subscription-overlay',
  templateUrl: './no-subscription-overlay.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, TranslateModule, ButtonModule],
})
export class NoSubscriptionOverlayComponent implements AfterViewInit, OnDestroy {
  readonly overlayRoutes = ['/instructions', '/instruction-templates', '/pictograms', '/display-settings'];

  loader = 'NoSubscriptionOverlayComponent';
  companySubscription?: CompanySubscription;
  isLicenceValid: boolean = true;
  showNoSubscriptionOverlay: boolean = false;
  overlayMessage: string = '';
  routerSub: Subscription;

  constructor(
    private companySubscriptionService: CompanySubscriptionService,
    private companyLicenceService: CompanyLicenceService,
    private toastsService: ToastsService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.refreshView());
  }

  ngAfterViewInit(): void {
    forkJoin({
      subscription: this.companySubscriptionService.getMyCompanySubscription(null),
      licenceValid: this.companyLicenceService.checkMyLicense(null),
    }).subscribe({
      next: (res) => {
        this.companySubscription = res.subscription;
        this.isLicenceValid = res.licenceValid.isValid;
        this.refreshView();
      },
      error: (error) => this.toastsService.showError(error),
    });
  }

  ngOnDestroy(): void {
    this.routerSub.unsubscribe();
  }

  refreshView() {
    const isCancelled = this.companySubscription?.status === CompanySubscriptionStatus.Cancelled;
    const isOverlayRoute = this.overlayRoutes.some((route) => this.router.url.includes(route));

    this.showNoSubscriptionOverlay = (isCancelled || !this.isLicenceValid) && isOverlayRoute;

    if (isCancelled) {
      this.overlayMessage = 'subscriptionAlert.missingSubscription';
    } else if (!this.isLicenceValid) {
      this.overlayMessage = 'subscriptionAlert.licenceLimitExceeded';
    }

    this.cdr.markForCheck();
  }
}
